/* eslint no-param-reassign: "off" */
import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import {
  SvgImage,
  ImageObject,
  Loader,
} from '../common';
import InlineGalleryItem from './InlineGalleryItem';
import InlineGalleryProps from './type';
import {
  isPortrait,
} from '../../utilities/browser';
import './styles.scss';

const ESC_KEY = 27;

interface SliderSettings {
  dots: boolean;
  arrows: boolean;
  fade: boolean;
  infinite: boolean;
  speed: number;
  slidesToShow: number;
  slidesToScroll: number;
  accessibility: boolean;
  centerMode: boolean;
  variableWidth: boolean;
  beforeChange: Function;
  onInit: Function;
}

const defaultSliderSettings: SliderSettings = {
  dots: false,
  arrows: false,
  fade: false,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  accessibility: true,
  centerMode: false,
  variableWidth: true,
  onInit: () => {},
  beforeChange: () => {},
};

const InlineGallery = ({
  images,
}: InlineGalleryProps) => {
  const slider = useRef<any>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const [showPrevious, setShowPrevious] = useState<boolean>(false);
  const [showNext, setShowNext] = useState<boolean>(images.length > 1);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isLoaded, setisLoaded] = useState<boolean>(false);
  const [sliderSettings, setSliderSettings] = useState<SliderSettings>({
    ...defaultSliderSettings,
    // We have to assign the slider callbacks separately as they
    // both have have reference (useRef elements) usages
    /* eslint-disable */
    onInit: () => onSliderInit(),
    beforeChange: (oldIndex: number, newIndex: number) => {
      onSliderBeforeChange(oldIndex, newIndex);
    },
    /* eslint-enable */
  });

  /**
   * More Slick slider hackery. This method calculators the
   * over all content width of the gallery so we can set max
   * widths on the container and the image (slide) placeholder.
   */
  const setMaxSlideWidth = () => {
    const slides: NodeListOf<HTMLDivElement> = (
      wrapper.current?.querySelectorAll('.inline-gallery__item') as NodeListOf<HTMLDivElement>
    );

    const viewportWidth: number = window.innerWidth;
    const containerWidth: number = (wrapper.current?.clientWidth as number);

    [].forEach.call(slides, (slide: HTMLDivElement) => {
      const slideImage = slide.querySelector('img');
      const imageWidth = slideImage?.offsetWidth as number;
      if (imageWidth >= viewportWidth) {
        // @ts-ignore
        slide?.parentNode?.parentNode?.style.maxWidth = `${containerWidth}px`;
        // @ts-ignore
        slide?.parentNode?.parentNode?.style.overflow = 'hidden';
      } else {
        // @ts-ignore
        slide?.parentNode?.parentNode?.style.maxWidth = `${imageWidth}px`;
      }
    });
  };

  /**
   * Callback function for slick slider initaliser. It checks
   * the images have loaded into the dom by checking the image
   * is laoded flag set by the image component. This is ran in
   * an interval until the images have loaded then in runs
   * setMaxSlideWidth ands clears the interval.
   */
  const onSliderInit = () => {
    const checkIfSlickIsLoaded = setInterval(() => {
      if (wrapper.current && wrapper.current?.querySelectorAll('.image-is-loaded').length === images.length) {
        setMaxSlideWidth();
        clearInterval(checkIfSlickIsLoaded);
        setisLoaded(true);
      }
    }, 300);
  };

  /**
   * Callback function for the slick before change event. This
   * handles the state for previous and next controls and sets
   * the current slide index in state.
   * @param oldIndex
   * @param newIndex
   */
  const onSliderBeforeChange = (oldIndex: number, newIndex: number) => {
    setTimeout(() => {
      setShowPrevious(Boolean(newIndex));
      setShowNext(Boolean((newIndex !== (images.length - 1))));
      setCurrentSlide(newIndex);
    }, 100);
  };

  /**
   * SlickSlider fugly hacky crap. This unsers the crap that
   * setMaxSlideWidth() sets :).
   */
  const resetMaxSlideWidth = () => {
    const slides = wrapper.current?.querySelectorAll('.inline-gallery__item');
    [].forEach.call(slides, (slide: HTMLElement) => {
      // @ts-ignore
      slide.querySelector('img').style.maxWidth = null;
      // @ts-ignore
      slide.parentNode.parentNode.style.maxWidth = null;
    });
  };

  /**
   * Toggle fullscreen state and changes the slider settings
   * accordinly.
   */
  const toggleFullscreen = () => {
    setIsFullScreen(!isFullScreen);
    setSliderSettings({
      ...sliderSettings,
      fade: !sliderSettings.fade,
    });
  };

  /**
   * Handles the next slide
   * @param event
   */
  const next = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (showNext) {
      slider.current?.slickNext();
    }
  };

  /**
   * Handles the previous slide
   * @param event
   */
  const previous = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (showPrevious) {
      slider.current?.slickPrev();
    }
  };

  /**
   * Changes the slide based on the index value passed.
   * @param event
   */
  const changeSlide = (index: number) => {
    slider.current?.slickGoTo(index);
  };

  /**
   * Component is mounted
   */
  useEffect(() => {
    // Set setMaxSlideWidth on window.resize
    window.addEventListener('resize', () => {
      resetMaxSlideWidth();
      setMaxSlideWidth();
    });

    // Add the keyup event listener to toggle the fullscreen mode
    document.addEventListener('keyup', (event) => {
      if (isFullScreen) {
        const key = event.key || event.keyCode;
        if (key === 'Escape' || key === 'Esc' || key === ESC_KEY) {
          setIsFullScreen(false);
          setSliderSettings({
            ...sliderSettings,
            fade: !sliderSettings.fade,
          });
        }
      }
    });
  }, []);

  return (
    <>
      {images && (
        <div className={`inline-gallery ${isFullScreen && 'inline-gallery--is-fullscreen'}`}>
          <div className="inline-gallery__inner">
            <div className="inline-gallery__content" ref={wrapper}>
              <div className="inline-gallery__items">
                {!isLoaded && (
                  <div className="inline-gallery__loader">
                    <Loader />
                  </div>
                )}
                <div className="inline-gallery__slider-container" style={{ opacity: isLoaded ? 1 : 0 }}>
                  {/* @ts-ignore */}
                  <Slider {...sliderSettings} ref={slider}>
                    {images.map((image) => (
                      <InlineGalleryItem
                        image={image}
                        handleClick={() => {}}
                      />
                    ))}
                  </Slider>
                </div>
              </div>
            </div>

            <div className="inline-gallery__pannel">
              <div className="inline-gallery__nav">
                <div className="inline-gallery__nav-controls">
                  <button className={`hero-slider__nav-previous ${!showPrevious && 'disabled'}`} onClick={(event: React.MouseEvent) => previous(event)}>
                    <span className="visually-hidden">Previous</span>
                    <SvgImage title="Previous slide" reference="#icon-right-paddle" />
                  </button>
                  <button className={`hero-slider__nav-next ${!showNext && 'disabled'}`} onClick={(event: React.MouseEvent) => next(event)}>
                    <span className="visually-hidden">Next</span>
                    <SvgImage title="Next slide" reference="#icon-right-paddle" />
                  </button>
                </div>
              </div>
              {images[currentSlide].title && (
                <div className="inline-gallery__caption">
                  {images[currentSlide].title}
                </div>
              )}

              {isFullScreen && (
                <>
                  <button className="overlay__close" onClick={() => toggleFullscreen()}>
                    <span className="visually-hidden">Close</span>
                    <SvgImage title="close icon" reference="#icon-close" />
                  </button>
                  <div className="inline-gallery__thumbnails">
                    <div className="inline-gallery__thumbnails-inner">
                      <ul>
                        {images.map((image, index) => (
                          <li className={`${index === currentSlide && 'active'}`}>
                            <button onClick={() => changeSlide(index)}>
                              <ImageObject image={image} widthSizes={['138']} fitType={isPortrait(image) ? 'contain' : 'cover'} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InlineGallery;
